import React from "react";
import Page from "../components/Page";
import {
  IconKey,
  IconLock,
  IconLink,
  IconRefresh,
  IconPlus,
  IconHistory,
  IconSettings,
  IconWifi,
  IconBluetooth,
  IconBattery,
  IconCheck,
  IconAlertCircle,
  IconClock,
  IconUser,
  IconBed
} from "@tabler/icons-react";
import { iconStroke } from "../config/config";
import { useRfidDashboard, syncRfidLocks } from "../controllers/rfid.controller";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";

export default function RfidDoorLockPage() {
  const { data: dashboard, error, mutate, isLoading } = useRfidDashboard();

  const handleSync = async () => {
    try {
      const res = await syncRfidLocks();
      toast.success(res.data?.message || "Locks synchronized successfully!");
      mutate();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Failed to sync locks");
    }
  };

  const locks = dashboard?.locks || { total: 0, gatewayEnabled: 0, icCardSupported: 0 };
  const rooms = dashboard?.rooms || { total: 0, mapped: 0, unmapped: 0 };
  const cards = dashboard?.cards || { total: 0, active: 0, expired: 0, lost: 0, issuedToday: 0 };
  const integration = dashboard?.integration || {};
  const recentOps = dashboard?.recentOperations || [];

  return (
    <Page className="p-4 md:p-6 space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 pb-4 border-b border-gray-200 dark:border-neutral-700">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-emerald-100 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 rounded-2xl">
            <IconKey stroke={iconStroke} size={28} />
          </div>
          <div>
            <h1 className="text-2xl font-black text-gray-900 dark:text-white">
              RFID Door Lock Integration
            </h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Sciener / TTLock Smart Lock Management, Room Mapping & Keycard Operations
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={handleSync}
            className="px-3.5 py-2 text-xs font-semibold rounded-xl border border-gray-300 dark:border-neutral-600 hover:bg-gray-50 dark:hover:bg-neutral-800 text-gray-700 dark:text-gray-200 flex items-center gap-1.5 transition"
          >
            <IconRefresh size={16} />
            <span>Sync Locks</span>
          </button>
          <Link
            to="/dashboard/rfid-doorlock/cards"
            className="px-4 py-2 text-xs font-semibold rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-1.5 shadow-sm transition"
          >
            <IconPlus size={16} />
            <span>Issue Keycard</span>
          </Link>
          <Link
            to="/dashboard/settings/rfid-doorlock"
            className="p-2 text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 rounded-xl hover:bg-gray-100 dark:hover:bg-neutral-800 transition"
            title="Integration Settings"
          >
            <IconSettings size={20} />
          </Link>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Total Locks */}
        <div className="p-4 rounded-2xl bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 shadow-sm">
          <div className="flex items-center justify-between text-gray-500 dark:text-gray-400">
            <span className="text-xs font-bold uppercase tracking-wider">Total Locks</span>
            <IconLock size={18} className="text-blue-500" />
          </div>
          <div className="text-3xl font-black text-gray-900 dark:text-white mt-2">
            {locks.total}
          </div>
          <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-2">
            <span>{locks.gatewayEnabled} Gateway</span> • <span>{locks.icCardSupported} IC Card</span>
          </div>
        </div>

        {/* Mapped Rooms */}
        <div className="p-4 rounded-2xl bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 shadow-sm">
          <div className="flex items-center justify-between text-gray-500 dark:text-gray-400">
            <span className="text-xs font-bold uppercase tracking-wider">Rooms Mapped</span>
            <IconBed size={18} className="text-emerald-500" />
          </div>
          <div className="text-3xl font-black text-gray-900 dark:text-white mt-2">
            {rooms.mapped} <span className="text-sm font-normal text-gray-400">/ {rooms.total}</span>
          </div>
          <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
            {rooms.unmapped > 0 ? (
              <span className="text-amber-600 font-semibold">{rooms.unmapped} unmapped room(s)</span>
            ) : (
              <span className="text-emerald-600 font-semibold">All rooms mapped</span>
            )}
          </div>
        </div>

        {/* Active Cards */}
        <div className="p-4 rounded-2xl bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 shadow-sm">
          <div className="flex items-center justify-between text-gray-500 dark:text-gray-400">
            <span className="text-xs font-bold uppercase tracking-wider">Active Cards</span>
            <IconKey size={18} className="text-purple-500" />
          </div>
          <div className="text-3xl font-black text-gray-900 dark:text-white mt-2">
            {cards.active}
          </div>
          <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
            {cards.issuedToday} issued today
          </div>
        </div>

        {/* Integration Status */}
        <div className="p-4 rounded-2xl bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 shadow-sm">
          <div className="flex items-center justify-between text-gray-500 dark:text-gray-400">
            <span className="text-xs font-bold uppercase tracking-wider">Connection</span>
            <div className={`w-2.5 h-2.5 rounded-full ${integration.connection_status === 'connected' ? 'bg-emerald-500' : 'bg-amber-500'}`} />
          </div>
          <div className="text-lg font-bold text-gray-900 dark:text-white mt-2 capitalize">
            {integration.connection_status || "Not Configured"}
          </div>
          <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-1 capitalize">
            Default: {integration.default_card_method || "Encoder"}
          </div>
        </div>
      </div>

      {/* Navigation Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Link
          to="/dashboard/rfid-doorlock/cards"
          className="p-3.5 rounded-2xl bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/20 dark:to-teal-950/20 border border-emerald-200/60 dark:border-emerald-800/40 hover:shadow-md transition flex items-center gap-3"
        >
          <div className="p-2 bg-emerald-600 text-white rounded-xl">
            <IconKey size={20} />
          </div>
          <div>
            <div className="text-xs font-bold text-gray-900 dark:text-white">Issue & Manage Cards</div>
            <div className="text-[10px] text-gray-500 dark:text-gray-400">Create, extend, or revoke keys</div>
          </div>
        </Link>

        <Link
          to="/dashboard/rfid-doorlock/mapping"
          className="p-3.5 rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 border border-blue-200/60 dark:border-blue-800/40 hover:shadow-md transition flex items-center gap-3"
        >
          <div className="p-2 bg-blue-600 text-white rounded-xl">
            <IconLink size={20} />
          </div>
          <div>
            <div className="text-xs font-bold text-gray-900 dark:text-white">Lock & Room Mapping</div>
            <div className="text-[10px] text-gray-500 dark:text-gray-400">Map locks to hotel doors</div>
          </div>
        </Link>

        <Link
          to="/dashboard/rfid-doorlock/history"
          className="p-3.5 rounded-2xl bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-950/20 dark:to-pink-950/20 border border-purple-200/60 dark:border-purple-800/40 hover:shadow-md transition flex items-center gap-3"
        >
          <div className="p-2 bg-purple-600 text-white rounded-xl">
            <IconHistory size={20} />
          </div>
          <div>
            <div className="text-xs font-bold text-gray-900 dark:text-white">Audit & History</div>
            <div className="text-[10px] text-gray-500 dark:text-gray-400">Card operations audit log</div>
          </div>
        </Link>

        <Link
          to="/dashboard/settings/rfid-doorlock"
          className="p-3.5 rounded-2xl bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20 border border-amber-200/60 dark:border-amber-800/40 hover:shadow-md transition flex items-center gap-3"
        >
          <div className="p-2 bg-amber-600 text-white rounded-xl">
            <IconSettings size={20} />
          </div>
          <div>
            <div className="text-xs font-bold text-gray-900 dark:text-white">Integration Settings</div>
            <div className="text-[10px] text-gray-500 dark:text-gray-400">API, USB encoder & policies</div>
          </div>
        </Link>
      </div>

      {/* Recent Operations Log */}
      <div className="bg-white dark:bg-neutral-800 rounded-2xl border border-gray-200 dark:border-neutral-700 p-5 shadow-sm">
        <div className="flex items-center justify-between pb-3 border-b border-gray-100 dark:border-neutral-700">
          <div>
            <h2 className="text-sm font-bold text-gray-900 dark:text-white">
              Recent Keycard Operations
            </h2>
            <p className="text-[11px] text-gray-500 dark:text-gray-400">
              Live audit record of recent card issuances, stay extensions, and revocations
            </p>
          </div>
          <Link
            to="/dashboard/rfid-doorlock/history"
            className="text-xs font-semibold text-emerald-600 hover:text-emerald-700"
          >
            View Full History →
          </Link>
        </div>

        <div className="mt-3 overflow-x-auto">
          {recentOps.length === 0 ? (
            <div className="py-8 text-center text-xs text-gray-500 dark:text-gray-400">
              No RFID card operations recorded yet.
            </div>
          ) : (
            <table className="w-full text-xs text-left">
              <thead>
                <tr className="border-b border-gray-200 dark:border-neutral-700 text-gray-500 dark:text-gray-400 font-semibold">
                  <th className="pb-2">Operation</th>
                  <th className="pb-2">Card #</th>
                  <th className="pb-2">Lock</th>
                  <th className="pb-2">Method</th>
                  <th className="pb-2">Operator</th>
                  <th className="pb-2">Status</th>
                  <th className="pb-2">Timestamp</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-neutral-700">
                {recentOps.map((op) => (
                  <tr key={op.id} className="hover:bg-gray-50 dark:hover:bg-neutral-700/40 transition">
                    <td className="py-2.5 font-bold capitalize text-gray-800 dark:text-gray-200">
                      {op.operation_type}
                    </td>
                    <td className="py-2.5 font-mono text-gray-600 dark:text-gray-300">
                      {op.card_number || "—"}
                    </td>
                    <td className="py-2.5 text-gray-800 dark:text-gray-200">
                      {op.lock_name || "—"}
                    </td>
                    <td className="py-2.5 capitalize text-gray-600 dark:text-gray-400">
                      {op.method || "—"}
                    </td>
                    <td className="py-2.5 text-gray-600 dark:text-gray-400">
                      {op.operator || "System"}
                    </td>
                    <td className="py-2.5">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                        op.status === 'success'
                          ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400'
                          : op.status === 'failed'
                          ? 'bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-400'
                          : 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400'
                      }`}>
                        {op.status}
                      </span>
                    </td>
                    <td className="py-2.5 text-gray-500 dark:text-gray-400">
                      {new Date(op.created_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </Page>
  );
}
